import { useState } from 'react';
import {
  Group,
  MultiSelect,
  Paper,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { LineChart } from '@mantine/charts';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  aggregatePerCompany,
  METRIC_LABELS,
  METRIC_OPTIONS,
  MetricOption,
  Period,
  USAGE_METRIC_KEYS,
  UsageMetricKey,
  UsageMetricRow,
} from './aggregate';

const CHART_COLORS = [
  'blue.6',
  'teal.6',
  'orange.6',
  'grape.6',
  'red.6',
  'lime.6',
  'cyan.6',
  'pink.6',
  'yellow.6',
  'indigo.6',
];

function colorFor(index: number) {
  return CHART_COLORS[index % CHART_COLORS.length];
}

export default function PerTenantPanel({
  rows,
  extraTotalKeys,
}: {
  rows: UsageMetricRow[];
  extraTotalKeys: readonly UsageMetricKey[];
}) {
  const [period, setPeriod] = useState<Period>('month');
  const [metric, setMetric] = useState<MetricOption>('total');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  const allCompanies = [...new Set(rows.map(row => row.company))].sort((a, b) =>
    a.localeCompare(b),
  );
  const activeCompanies = selectedCompanies.length > 0 ? selectedCompanies : allCompanies;
  const filteredRows = rows.filter(row => activeCompanies.includes(row.company));

  const chartPoints = aggregatePerCompany(filteredRows, period, extraTotalKeys);
  const periodOrder = [...new Map(chartPoints.map(p => [p.periodLabel, p.periodStart])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([label]) => label);

  const chartData = periodOrder.map(periodLabel => {
    const entry: Record<string, string | number> = { period: periodLabel };
    for (const company of activeCompanies) {
      const point = chartPoints.find(p => p.company === company && p.periodLabel === periodLabel);
      if (!point) {
        entry[company] = 0;
      } else {
        entry[company] = metric === 'total' ? point.total : point.totals[metric];
      }
    }
    return entry;
  });

  const series = activeCompanies.map((company, index) => ({
    name: company,
    color: colorFor(index),
  }));

  const monthlyRows = aggregatePerCompany(filteredRows, 'month', extraTotalKeys)
    .filter(point => point.total > 0)
    .sort(
      (a, b) => a.company.localeCompare(b.company) || a.periodStart.localeCompare(b.periodStart),
    );

  return (
    <Stack gap="lg">
      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Text size="sm" fw={FONT_WEIGHTS.semibold}>
              Usage per tenant
            </Text>
            <Group gap="xs" wrap="wrap">
              <MultiSelect
                size="xs"
                w={260}
                placeholder="All companies"
                data={allCompanies}
                value={selectedCompanies}
                onChange={setSelectedCompanies}
                searchable
                clearable
              />
              <Select
                size="xs"
                w={190}
                value={metric}
                onChange={value => setMetric((value as MetricOption | null) ?? 'total')}
                data={METRIC_OPTIONS}
                allowDeselect={false}
              />
              <SegmentedControl
                size="xs"
                value={period}
                onChange={value => setPeriod(value as Period)}
                data={[
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' },
                ]}
              />
            </Group>
          </Group>
          {chartData.length === 0 || series.length === 0 ? (
            <Text size="sm" c="dimmed">
              No usage recorded yet for this filter.
            </Text>
          ) : (
            <LineChart
              h={340}
              data={chartData}
              dataKey="period"
              series={series}
              curveType="monotone"
              withDots={false}
              withLegend
              gridAxis="x"
            />
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={FONT_WEIGHTS.semibold}>
            Per-company monthly totals
          </Text>
          <Table.ScrollContainer minWidth={760}>
            <Table striped highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Company</Table.Th>
                  <Table.Th>Month</Table.Th>
                  {USAGE_METRIC_KEYS.map(key => (
                    <Table.Th key={key} ta="right">
                      {METRIC_LABELS[key]}
                    </Table.Th>
                  ))}
                  <Table.Th ta="right">Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {monthlyRows.map(row => (
                  <Table.Tr key={`${row.company}-${row.periodKey}`}>
                    <Table.Td>{row.company}</Table.Td>
                    <Table.Td>{row.periodLabel}</Table.Td>
                    {USAGE_METRIC_KEYS.map(key => (
                      <Table.Td key={key} ta="right">
                        {row.totals[key]}
                      </Table.Td>
                    ))}
                    <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                      {row.total}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {monthlyRows.length === 0 && (
            <Text size="sm" c="dimmed" ta="center" py="xs">
              No usage recorded yet for this filter.
            </Text>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
